import React, { useState, useContext, useCallback, useMemo } from 'react';
import {
  H1,
  Text,
  Flex,
  Select,
  Option,
  FlexItem,
  TimePicker,
  FormContextType,
  FormContext,
  FormItem,
  Input,
  styled,
} from '@erecruit/ui-kit';
import {
  Mode as CalendarMode,
  Modifier as CalendarModifier,
} from '@erecruit/ui-kit/src/molecules/Calendar/Calendar';
import { Intl } from '@erecruit/core';
import { connect } from 'react-redux';
import moment from 'moment';
import { Resources } from '@erecruit/types';

import { AddNewShiftInitialData } from '../../AddNewShiftWizard';
import { RootState } from '../../../../redux/reducers';
import { DatesSelectionInfo } from './components/DatesSelectionInfo';
import { DatesPicker } from './components/DatesPicker';
import { getDefaultValue } from './utilities';
import { DEFAULT_SHIFT_FORMAT } from '../../../../constants';
import { AddNewShiftWizardFields } from '../../types';

const { FormattedMessage, injectIntl } = Intl;

interface SelectShiftsStepProps {
  formData: AddNewShiftInitialData;
  isLoading: boolean;
}

const MaxedFlex = styled(Flex)({
  maxHeight: '60vh',
});

function getFormOptions(initialValue?: unknown) {
  return {
    initialValue: initialValue,
    rules: [
      {
        required: true,
        message: (
          <FormattedMessage id={messageIds.vms.errors.form.requiredField} />
        ),
      },
    ],
  };
}

export function asFormItem(
  id: string,
  localizedLabel: Intl.LocalizationKeysArray,
  initialValue?: unknown,
) {
  return (node: React.ReactNode) => (
    <FormItem
      id={id}
      label={<FormattedMessage id={localizedLabel} />}
      options={getFormOptions(initialValue)}
    >
      {node}
    </FormItem>
  );
}

const calculateShiftTime = (shiftType: Resources.ShiftType) => ({
  startTime: moment(shiftType.startTime || '8:00:00', DEFAULT_SHIFT_FORMAT),
  endTime: moment(shiftType.endTime || '16:00:00', DEFAULT_SHIFT_FORMAT),
});

export const SelectDatesForm = injectIntl(
  ({
    top,
    bottom,
    left,
    right,
    intl,
  }: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  } & Intl.InjectedIntlProps) => (
    <FormItem
      style={{
        position: 'absolute',
        bottom: bottom,
        top: top,
        left: left,
        right: right,
      }}
      id="selectedDates"
      label={''}
      options={{
        initialValue: {},
        rules: [
          {
            message: intl.formatMessage({
              id: messageIds.vms.newShiftWizard.addShift.noDatesSelected,
            }),
            validator: (rules, value) => {
              if (Object.keys(value).length === 0) {
                return new Error('At least one day needs to be selected!');
              }
            },
          },
        ],
      }}
    >
      <span></span>
    </FormItem>
  ),
);

const messages = messageIds.vms.newShiftWizard.general;

const SelectShiftsStep = ({ formData }: SelectShiftsStepProps) => {
  const { departments, shiftTypes } = formData;
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('multiple');

  const getShift = (shiftTypeId: number) =>
    shiftTypes && shiftTypes.find(shift => shift.id === shiftTypeId);

  const form = useContext<FormContextType<AddNewShiftWizardFields>>(
    FormContext,
  );

  const formFields = form.getFieldsValue();

  const selectedDates = formFields.selectedDates || {};
  const selectedDatesQuantities = formFields.selectedDatesQuantities || {};
  const selectionPerType = formFields.selectionPerType || {
    multiple: [],
    range: [],
    single: [],
  };

  const setSelectedDates = useCallback(
    (selected: Record<string, CalendarModifier>) => {
      form.setFieldsValue({
        selectedDates: selected,
      });
    },
    [form],
  );

  const setSelectedDatesQuantities = useCallback(
    (selected: Record<string, number>) => {
      form.setFieldsValue({
        selectedDatesQuantities: selected,
      });
    },
    [form],
  );

  const setSelectionPerType = useCallback(
    (selected: Record<string, string[]>) => {
      form.setFieldsValue({
        selectionPerType: selected,
      });
    },
    [form],
  );

  const onDateQuantityChange = (key: string, newQuantity: number) => {
    const newSelectedDaysQuantities = { ...selectedDatesQuantities };
    newSelectedDaysQuantities[key] = newQuantity;
    setSelectedDatesQuantities(newSelectedDaysQuantities);
  };

  const onDateRemove = (key: string) => {
    const newSelectedDatesQuantities = { ...selectedDatesQuantities };
    const newSelectedDates = { ...selectedDates };
    const newSelectionPerType = { ...selectionPerType };

    delete newSelectedDatesQuantities[key];
    delete newSelectedDates[key];

    const allKeys = Object.keys(newSelectedDates);

    newSelectionPerType['range'] = allKeys.filter(
      key => key.indexOf('-') !== -1,
    );
    newSelectionPerType['multiple'] = allKeys.filter(
      key => key.indexOf('-') === -1,
    );

    setSelectedDatesQuantities(newSelectedDatesQuantities);
    setSelectedDates(newSelectedDates);
    setSelectionPerType(newSelectionPerType);
  };

  const handleShiftTypeChange = (shiftTypeId: number) => {
    const shift = getShift(shiftTypeId);
    const { startTime, endTime } = shift
      ? calculateShiftTime(shift)
      : {
          startTime: undefined,
          endTime: undefined,
        };

    form.setFieldsValue({
      startTime,
      endTime,
    });
  };

  const initialShiftTypeRange = useMemo(() => {
    if (!shiftTypes || shiftTypes.length === 0 || shiftTypes.length > 1) {
      return {
        startTime: undefined,
        endTime: undefined,
      };
    }

    return calculateShiftTime(shiftTypes[0]);
  }, [shiftTypes]);

  return (
    <>
      <FlexItem flex={0} gutter={2} gutterDirection="vertical">
        <H1>
          <FormattedMessage id={messageIds.vms.newShiftWizard.addShift.title} />
        </H1>
        <Text>
          <FormattedMessage
            id={messageIds.vms.newShiftWizard.addShift.subTitle}
          />
        </Text>
      </FlexItem>
      <MaxedFlex padding={[6, 0, 0, 0]} gutter={2} flex={0} stretch>
        <div style={{ visibility: 'hidden', position: 'absolute' }}>
          {asFormItem(
            'selectedDatesQuantities',
            messageIds.vms.newShiftWizard.title,
            {},
          )(<Input />)}
          {asFormItem(
            'selectionPerType',
            messageIds.vms.newShiftWizard.title,
            {},
          )(<Input />)}
        </div>
        <FlexItem basis="50%" flex={0}>
          <SelectDatesForm bottom={70} left={85} />
          <DatesPicker
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            selectedDatesQuantities={selectedDatesQuantities}
            setSelectedDatesQuantities={setSelectedDatesQuantities}
            selectionPerType={selectionPerType}
            setSelectionPerType={setSelectionPerType}
            calendarMode={calendarMode}
            setCalendarMode={setCalendarMode}
          />
        </FlexItem>
        <FlexItem padding={[0, 6, 0, 10]} flex={1} stretch>
          <Flex
            direction="column"
            gutter={0}
            gutterDirection="vertical"
            stretch
          >
            <FlexItem flex={0} padding={[0, 12, 0, 0]}>
              {asFormItem(
                'departmentId',
                messages.department,
                formFields.departmentId || getDefaultValue(departments),
              )(
                <Select disabled={departments.length === 1}>
                  {departments.map(department => (
                    <Option key={department.id} value={department.id}>
                      {department.name}
                    </Option>
                  ))}
                </Select>,
              )}
            </FlexItem>
            <FlexItem flex={0} padding={[0, 12, 0, 0]}>
              {asFormItem(
                'shiftTypeId',
                messages.shiftType,
                formFields.shiftTypeId || getDefaultValue(shiftTypes),
              )(
                <Select
                  onSelect={(value: number) => handleShiftTypeChange(value)}
                  disabled={shiftTypes.length === 1}
                >
                  {shiftTypes.map(shiftType => (
                    <Option key={shiftType.id} value={shiftType.id}>
                      {shiftType.name}
                    </Option>
                  ))}
                </Select>,
              )}
            </FlexItem>
            <FlexItem flex={0} padding={[0, 12, 0, 0]}>
              <Flex gutter={4} gutterDirection="horizontal">
                <FlexItem>
                  {asFormItem(
                    'startTime',
                    messages.startTime,
                    initialShiftTypeRange.startTime,
                  )(
                    <TimePicker
                      use12Hours={true}
                      minuteStep={15}
                      defaultOpenValue={
                        initialShiftTypeRange.startTime ||
                        moment().set({ minute: 0, second: 0 })
                      }
                    />,
                  )}
                </FlexItem>
                <FlexItem>
                  {asFormItem(
                    'endTime',
                    messages.endTime,
                    initialShiftTypeRange.endTime,
                  )(
                    <TimePicker
                      use12Hours={true}
                      minuteStep={15}
                      defaultOpenValue={
                        initialShiftTypeRange.endTime ||
                        moment().set({ minute: 0, second: 0 })
                      }
                    />,
                  )}
                </FlexItem>
              </Flex>
            </FlexItem>
            <FlexItem padding={[4, 0]} style={{ overflowY: 'auto' }} flex={1}>
              <DatesSelectionInfo
                selectedDates={selectedDates}
                selectedDatesQuantities={selectedDatesQuantities}
                onDateQuantityChange={onDateQuantityChange}
                onDateRemove={onDateRemove}
              />
            </FlexItem>
          </Flex>
        </FlexItem>
      </MaxedFlex>
    </>
  );
};

const ConnectedSelectShiftsStep = connect((state: RootState) => ({
  isLoading: state.app.addNewShift.isLoading,
}))(SelectShiftsStep);

export { ConnectedSelectShiftsStep as AddShiftsStep };
